import React, { useState, useMemo, useCallback } from 'react';
import { Wrench, DollarSign, FileText, ChevronUp, ChevronDown, Camera, X, ImageOff, Check, Trash2, ThumbsDown, CheckSquare } from 'lucide-react';
import { getAssetImageUrl } from '../utils/assetImageUrl';
import { isCustomerContext, isVendorContext } from '../utils/repairContext';

/**
 * RepairListView — shared list-view component for the Repairs page (Asset Tracking)
 * and the Workflows page (Workflows module).
 *
 * Props:
 *   filteredRepairs       – array of repair objects already filtered by search/vendor/company
 *   columns               – array of { id, title, color } from parent's `columns` useMemo
 *   isDark                – boolean from useTheme()
 *   onTicketClick         – (ticket) => void  — opens existing DetailsModal
 *   effectiveIsCustomer   – boolean
 *   customerRepairTotals  – { totalInRepairAndComplete, totalNeedsApproval }
 */
const RepairListView = ({
  filteredRepairs = [],
  columns = [],
  isDark,
  onTicketClick,
  effectiveIsCustomer,
  effectiveIsAssignedVendor,
  customerRepairTotals = { totalInRepairAndComplete: 0, totalNeedsApproval: 0 },
  repairFiles = {},
  loadRepairFiles,
  moveRepair,
  user,
  onFillInspection,   // (ticket) => void — opens vendor inspection form for Needs Repair rows
  // Controlled status filter — when provided, the internal tiles are hidden
  // and the parent is responsible for rendering them.
  statusFilterControlled,
  onStatusFilterChange,
}) => {
  // ── Status tile filter ──────────────────────────────────────────────────────
  const [statusFilterInternal, setStatusFilterInternal] = useState('all');
  const isControlled = statusFilterControlled !== undefined;
  const statusFilter = isControlled ? statusFilterControlled : statusFilterInternal;
  const setStatusFilter = isControlled ? onStatusFilterChange : setStatusFilterInternal;

  // ── Checkbox / bulk selection state ─────────────────────────────────────────
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkLoading, setBulkLoading] = useState(false);

  // ── Image gallery modal state ────────────────────────────────────────────────
  const [galleryTicket, setGalleryTicket] = useState(null);   // ticket whose images to show
  const [galleryLoading, setGalleryLoading] = useState(false);
  const [lightboxUrl, setLightboxUrl] = useState(null);       // full-screen single image

  const openGallery = useCallback(async (e, ticket) => {
    e.stopPropagation(); // don't trigger row click → DetailsModal
    setGalleryTicket(ticket);
    // Load files if not already loaded
    if (loadRepairFiles && (!repairFiles[ticket.id] || repairFiles[ticket.id].length === 0)) {
      setGalleryLoading(true);
      try {
        await loadRepairFiles(ticket.id);
      } finally {
        setGalleryLoading(false);
      }
    }
  }, [loadRepairFiles, repairFiles]);

  // ── Checkbox helpers ─────────────────────────────────────────────────────────
  const toggleRow = useCallback((e, id) => {
    e.stopPropagation();
    setSelectedIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }, []);

  // Bulk action executor — calls existing moveRepair for each id in the provided list
  // idsOverride: explicit array of ids to act on (subset of selection); falls back to all selectedIds
  const executeBulk = useCallback(async (newStatus, confirmMsg, idsOverride) => {
    if (!moveRepair) return;
    const ids = idsOverride ?? [...selectedIds];
    if (ids.length === 0) return;
    if (confirmMsg && !window.confirm(confirmMsg)) return;
    setBulkLoading(true);
    try {
      await Promise.all(ids.map(id => moveRepair(id, newStatus)));
      setSelectedIds(new Set());
    } catch (err) {
      console.error('Bulk action failed:', err);
      alert('One or more repairs could not be updated. Please try again.');
    } finally {
      setBulkLoading(false);
    }
  }, [moveRepair, selectedIds]);

  // ── Sort state ──────────────────────────────────────────────────────────────
  // NOTE: Only client-side sort on already-loaded data. No new backend calls.
  const [sortField, setSortField] = useState(null);
  const [sortDir, setSortDir] = useState('asc');

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  // ── Tile counts ─────────────────────────────────────────────────────────────
  const tileCounts = useMemo(() => {
    const counts = { all: filteredRepairs.length };
    columns.forEach(col => {
      counts[col.id] = filteredRepairs.filter(r => r.status === col.id).length;
    });
    return counts;
  }, [filteredRepairs, columns]);

  // ── Apply status tile filter ─────────────────────────────────────────────────
  const statusFilteredRepairs = useMemo(() => {
    if (statusFilter === 'all') return filteredRepairs;
    return filteredRepairs.filter(r => r.status === statusFilter);
  }, [filteredRepairs, statusFilter]);

  // ── Apply sort ───────────────────────────────────────────────────────────────
  const sortedRepairs = useMemo(() => {
    if (!sortField) return statusFilteredRepairs;
    return [...statusFilteredRepairs].sort((a, b) => {
      let aVal, bVal;
      if (sortField === 'cost') {
        aVal = parseFloat(a.total_repair_cost) || 0;
        bVal = parseFloat(b.total_repair_cost) || 0;
      } else if (sortField === 'status') {
        aVal = a.status || '';
        bVal = b.status || '';
      } else if (sortField === 'asset') {
        aVal = (a.asset_name || a.asset_id || '').toLowerCase();
        bVal = (b.asset_name || b.asset_id || '').toLowerCase();
      } else if (sortField === 'customer') {
        aVal = (a.company_name || '').toLowerCase();
        bVal = (b.company_name || '').toLowerCase();
      } else {
        return 0;
      }
      if (aVal < bVal) return sortDir === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
  }, [statusFilteredRepairs, sortField, sortDir]);

  // ── Per-ticket vendor/customer helpers (with effectiveIs* fallbacks) ─────────
  // isVendorContext checks user.organizationId === ticket.vendor_id, but in the
  // Workflows module the page-level effectiveIsAssignedVendor is the reliable flag.
  const isVendorForTicket = useCallback((t) => {
    return isVendorContext(user, t) || effectiveIsAssignedVendor;
  }, [user, effectiveIsAssignedVendor]);

  const isCustomerForTicket = useCallback((t) => {
    return isCustomerContext(user, t) || (effectiveIsCustomer && !effectiveIsAssignedVendor);
  }, [user, effectiveIsCustomer, effectiveIsAssignedVendor]);

  // ── Select-all: only rows the current user can act on ───────────────────────
  const actionableIds = useMemo(() => {
    if (!moveRepair || !user) return [];
    return sortedRepairs
      .filter(t => {
        const isC = isCustomerForTicket(t);
        const isV = isVendorForTicket(t);
        // Customer: can approve/decline Needs Approval repairs
        // Vendor: can start work on Needs Approval repairs, complete/scrap In Repair repairs, or scrap Needs Repair repairs
        return (isC && t.status === 'Needs Approval') ||
               (isV && t.status === 'Needs Approval') ||
               (isV && t.status === 'In Repair') ||
               (isV && t.status === 'Needs Repair');
      })
      .map(t => t.id);
  }, [sortedRepairs, user, moveRepair, isVendorForTicket, isCustomerForTicket]);

  const allActionableSelected = actionableIds.length > 0 &&
    actionableIds.every(id => selectedIds.has(id));

  const toggleSelectAll = useCallback((e) => {
    e.stopPropagation();
    if (allActionableSelected) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(actionableIds));
    }
  }, [allActionableSelected, actionableIds]);

  // ── Derive what bulk actions are available for the current selection ─────────
  const bulkActions = useMemo(() => {
    if (selectedIds.size === 0 || !moveRepair) return [];
    const selected = sortedRepairs.filter(t => selectedIds.has(t.id));
    const actions = [];

    // ── Customer actions: Needs Approval rows they own ────────────────────────
    const needsApprovalOwned = selected.filter(t =>
      isCustomerForTicket(t) && t.status === 'Needs Approval'
    );
    if (needsApprovalOwned.length > 0) {
      const ids = needsApprovalOwned.map(t => t.id);
      actions.push({
        label: `Approve (${needsApprovalOwned.length})`,
        icon: Check,
        color: 'bg-green-600 hover:bg-green-500 text-white',
        onClick: () => executeBulk('In Repair', null, ids),
      });
      actions.push({
        label: `Decline (${needsApprovalOwned.length})`,
        icon: ThumbsDown,
        color: 'bg-red-600/20 hover:bg-red-600 text-red-400 hover:text-white border border-red-500/50',
        onClick: () => executeBulk('Sent to Vendor',
          `Decline ${needsApprovalOwned.length} repair(s)? They will be sent back to the vendor.`, ids),
      });
    }

    // ── Vendor actions: Needs Approval → Start Work (verbal approval) ─────────
    const needsApprovalVendor = selected.filter(t =>
      isVendorForTicket(t) && t.status === 'Needs Approval'
    );
    if (needsApprovalVendor.length > 0) {
      const ids = needsApprovalVendor.map(t => t.id);
      actions.push({
        label: `Start Work (${needsApprovalVendor.length})`,
        icon: Wrench,
        color: 'bg-amber-600 hover:bg-amber-500 text-white',
        onClick: () => executeBulk('In Repair',
          `Start work on ${needsApprovalVendor.length} repair(s) from verbal approval? Owner approval is still pending.`, ids),
      });
    }

    // ── Vendor actions: In Repair → Mark Complete / Scrap ─────────────────────
    const inRepairAssigned = selected.filter(t =>
      isVendorForTicket(t) && t.status === 'In Repair'
    );
    if (inRepairAssigned.length > 0) {
      const ids = inRepairAssigned.map(t => t.id);
      actions.push({
        label: `Mark Complete (${inRepairAssigned.length})`,
        icon: Check,
        color: 'bg-emerald-600 hover:bg-emerald-500 text-white',
        onClick: () => executeBulk('Complete', null, ids),
      });
      actions.push({
        label: `Scrap (${inRepairAssigned.length})`,
        icon: Trash2,
        color: 'bg-red-600/20 hover:bg-red-600 text-red-400 hover:text-white border border-red-500/50',
        onClick: () => executeBulk('Scrapped',
          `Scrap ${inRepairAssigned.length} repair(s)? This cannot be undone.`, ids),
      });
    }

    // ── Vendor actions: Needs Repair → Scrap ──────────────────────────────────
    const needsRepairAssigned = selected.filter(t =>
      isVendorForTicket(t) && t.status === 'Needs Repair'
    );
    if (needsRepairAssigned.length > 0) {
      const ids = needsRepairAssigned.map(t => t.id);
      actions.push({
        label: `Scrap (${needsRepairAssigned.length})`,
        icon: Trash2,
        color: 'bg-red-600/20 hover:bg-red-600 text-red-400 hover:text-white border border-red-500/50',
        onClick: () => executeBulk('Scrapped',
          `Scrap ${needsRepairAssigned.length} repair(s)? This cannot be undone.`, ids),
      });
    }

    return actions;
  }, [selectedIds, sortedRepairs, moveRepair, executeBulk, isCustomerForTicket, isVendorForTicket]);

  // ── Helpers ──────────────────────────────────────────────────────────────────
  const getStatusColor = (status) => {
    switch (status) {
      case 'Needs Repair':
      case 'Sent to Vendor':
        return isDark
          ? 'bg-red-500/20 text-red-300 border border-red-500/40'
          : 'bg-red-100 text-red-700 border border-red-200';
      case 'Needs Approval':
        return isDark
          ? 'bg-yellow-500/20 text-yellow-300 border border-yellow-500/40'
          : 'bg-yellow-100 text-yellow-700 border border-yellow-200';
      case 'In Repair':
        return isDark
          ? 'bg-blue-500/20 text-blue-300 border border-blue-500/40'
          : 'bg-blue-100 text-blue-700 border border-blue-200';
      case 'Complete':
        return isDark
          ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/40'
          : 'bg-emerald-100 text-emerald-700 border border-emerald-200';
      default:
        return isDark
          ? 'bg-slate-700 text-slate-300 border border-slate-600'
          : 'bg-gray-100 text-gray-600 border border-gray-200';
    }
  };

  const getTileAccentColor = (colId) => {
    switch (colId) {
      case 'Needs Repair':
      case 'Sent to Vendor':
        return isDark ? 'border-red-500/60 hover:border-red-400' : 'border-red-300 hover:border-red-500';
      case 'Needs Approval':
        return isDark ? 'border-yellow-500/60 hover:border-yellow-400' : 'border-yellow-300 hover:border-yellow-500';
      case 'In Repair':
        return isDark ? 'border-blue-500/60 hover:border-blue-400' : 'border-blue-300 hover:border-blue-500';
      case 'Complete':
        return isDark ? 'border-emerald-500/60 hover:border-emerald-400' : 'border-emerald-300 hover:border-emerald-500';
      default:
        return isDark ? 'border-slate-600 hover:border-slate-400' : 'border-gray-200 hover:border-gray-400';
    }
  };

  const getTileActiveBg = (colId) => {
    switch (colId) {
      case 'Needs Repair':
      case 'Sent to Vendor':
        return isDark ? 'bg-red-500/25 border-red-400' : 'bg-red-100 border-red-500';
      case 'Needs Approval':
        return isDark ? 'bg-yellow-500/25 border-yellow-400' : 'bg-yellow-100 border-yellow-500';
      case 'In Repair':
        return isDark ? 'bg-blue-500/25 border-blue-400' : 'bg-blue-100 border-blue-500';
      case 'Complete':
        return isDark ? 'bg-emerald-500/25 border-emerald-400' : 'bg-emerald-100 border-emerald-500';
      default:
        return isDark ? 'bg-slate-700 border-slate-400' : 'bg-gray-100 border-gray-400';
    }
  };

  // Get damaged checklist items from a repair's damage_details
  const getDamagedItems = (ticket) => {
    try {
      const repairData = ticket?.repair_details || ticket?.damage_details;
      if (!repairData) return [];
      const parsed = typeof repairData === 'string' ? JSON.parse(repairData) : repairData;
      const checklist = parsed?.checklist;
      if (Array.isArray(checklist)) {
        return checklist.filter(item => item.status === 'Damaged' || item.status === 'damaged');
      }
      // Fallback: items array
      if (Array.isArray(parsed?.items)) {
        return parsed.items.filter(item => item.cost > 0);
      }
    } catch {
      // ignore parse errors
    }
    return [];
  };

  const SortIcon = ({ field }) => {
    if (sortField !== field) return <ChevronUp size={12} className="opacity-30" />;
    return sortDir === 'asc'
      ? <ChevronUp size={12} className="opacity-80" />
      : <ChevronDown size={12} className="opacity-80" />;
  };

  const SortableHeader = ({ field, label, className = '' }) => (
    <th
      className={`px-3 py-3 text-left text-xs font-semibold uppercase tracking-wide cursor-pointer select-none whitespace-nowrap ${isDark ? 'text-slate-400 hover:text-slate-200' : 'text-gray-500 hover:text-gray-800'} ${className}`}
      onClick={() => handleSort(field)}
    >
      <span className="flex items-center gap-1">
        {label}
        <SortIcon field={field} />
      </span>
    </th>
  );

  // ── Tile dollar totals (only shown for customer view) ────────────────────────
  // "Pending Approval $" → totalNeedsApproval (filtered by current statusFilter)
  // "Total (In Repair & Complete) $" → totalInRepairAndComplete (filtered)
  const filteredNeedsApprovalTotal = useMemo(() => {
    const base = statusFilter === 'all' || statusFilter === 'Needs Approval'
      ? statusFilteredRepairs.filter(r => r.status === 'Needs Approval')
      : [];
    return base.reduce((s, r) => s + (parseFloat(r.total_repair_cost) || 0), 0);
  }, [statusFilteredRepairs, statusFilter]);

  const filteredInRepairCompleteTotal = useMemo(() => {
    const base = statusFilteredRepairs.filter(r => r.status === 'In Repair' || r.status === 'Complete');
    return base.reduce((s, r) => s + (parseFloat(r.total_repair_cost) || 0), 0);
  }, [statusFilteredRepairs]);

  return (
    <div className="flex flex-col h-full min-h-0">
      {/* ── Status Tiles — hidden when parent controls the filter ─────────────── */}
      {!isControlled && <div className="flex flex-wrap gap-2 mb-4 shrink-0">
        {/* "All" tile */}
        <button
          onClick={() => setStatusFilter('all')}
          className={`flex flex-col items-start px-4 py-3 rounded-xl border-2 transition-all min-w-[90px] ${
            statusFilter === 'all'
              ? isDark
                ? 'bg-slate-700 border-slate-400 text-white'
                : 'bg-slate-100 border-slate-500 text-slate-900'
              : isDark
                ? 'bg-slate-800/50 border-slate-700 text-slate-300 hover:border-slate-500'
                : 'bg-white border-gray-200 text-gray-700 hover:border-gray-400'
          }`}
        >
          <span className="text-2xl font-bold leading-none">{tileCounts.all}</span>
          <span className="text-xs mt-1 font-medium">All</span>
        </button>

        {/* Per-status tiles */}
        {columns.map(col => {
          const isActive = statusFilter === col.id;
          return (
            <button
              key={col.id}
              onClick={() => setStatusFilter(isActive ? 'all' : col.id)}
              className={`flex flex-col items-start px-4 py-3 rounded-xl border-2 transition-all min-w-[110px] ${
                isActive
                  ? getTileActiveBg(col.id)
                  : `${isDark ? 'bg-slate-800/50' : 'bg-white'} ${getTileAccentColor(col.id)}`
              }`}
            >
              <span className={`text-2xl font-bold leading-none ${isDark ? 'text-white' : 'text-slate-900'}`}>
                {tileCounts[col.id] ?? 0}
              </span>
              <span className={`text-xs mt-1 font-medium ${isDark ? 'text-slate-300' : 'text-slate-600'}`}>
                {col.title}
              </span>
              {/* Dollar total on relevant tiles (customer view only) */}
              {effectiveIsCustomer && col.id === 'Needs Approval' && (
                <span className={`text-xs mt-0.5 font-semibold ${isDark ? 'text-yellow-400' : 'text-yellow-600'}`}>
                  ${(isActive ? filteredNeedsApprovalTotal : customerRepairTotals.totalNeedsApproval).toFixed(2)}
                </span>
              )}
              {effectiveIsCustomer && (col.id === 'In Repair' || col.id === 'Complete') && (
                <span className={`text-xs mt-0.5 font-semibold ${isDark ? 'text-emerald-400' : 'text-emerald-600'}`}>
                  ${(isActive ? filteredInRepairCompleteTotal : customerRepairTotals.totalInRepairAndComplete).toFixed(2)}
                </span>
              )}
            </button>
          );
        })}
      </div>}

      {/* ── Bulk Action Bar — appears when ≥1 actionable row is checked ─────── */}
      {selectedIds.size > 0 && bulkActions.length > 0 && (
        <div className={`flex items-center gap-3 px-4 py-2.5 rounded-xl border mb-2 shrink-0 ${
          isDark
            ? 'bg-slate-800 border-slate-600'
            : 'bg-blue-50 border-blue-200'
        }`}>
          <span className={`text-sm font-medium flex items-center gap-1.5 ${isDark ? 'text-slate-300' : 'text-slate-700'}`}>
            <CheckSquare size={15} />
            {selectedIds.size} selected
          </span>
          <div className="flex gap-2 flex-wrap">
            {bulkActions.map((action, i) => {
              const Icon = action.icon;
              return (
                <button
                  key={i}
                  onClick={action.onClick}
                  disabled={bulkLoading}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${action.color}`}
                >
                  <Icon size={14} />
                  {action.label}
                </button>
              );
            })}
          </div>
          <button
            onClick={() => setSelectedIds(new Set())}
            className={`ml-auto text-xs px-2 py-1 rounded transition-colors ${isDark ? 'text-slate-400 hover:text-slate-200 hover:bg-slate-700' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'}`}
          >
            Clear
          </button>
        </div>
      )}

      {/* ── Table ─────────────────────────────────────────────────────────────── */}
      <div className={`flex-1 overflow-auto rounded-xl border ${isDark ? 'border-slate-700' : 'border-gray-200'}`}>
        <table className="w-full min-w-[700px] border-collapse">
          <thead className={`sticky top-0 z-10 ${isDark ? 'bg-slate-900' : 'bg-gray-50'}`}>
            <tr className={`border-b ${isDark ? 'border-slate-700' : 'border-gray-200'}`}>
              {/* Select-all checkbox — selects only rows the current user can act on */}
              <th className="w-10 px-3 py-3" onClick={e => e.stopPropagation()}>
                {moveRepair && actionableIds.length > 0 ? (
                  <input
                    type="checkbox"
                    checked={allActionableSelected}
                    onChange={toggleSelectAll}
                    title={`Select all actionable repairs (${actionableIds.length})`}
                    className="rounded cursor-pointer accent-blue-500"
                  />
                ) : (
                  <input
                    type="checkbox"
                    disabled
                    title="No actionable repairs in current view"
                    className="rounded opacity-30 cursor-not-allowed"
                  />
                )}
              </th>
              <th className={`px-3 py-3 text-left text-xs font-semibold uppercase tracking-wide ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
                Asset
              </th>
              <SortableHeader field="customer" label="Customer" />
              <SortableHeader field="status" label="Status" />
              <SortableHeader field="cost" label="Cost" />
              <th className={`px-3 py-3 text-left text-xs font-semibold uppercase tracking-wide ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
                Description
              </th>
              <th className={`px-3 py-3 text-left text-xs font-semibold uppercase tracking-wide ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
                Damaged Items
              </th>
              <th className={`px-3 py-3 text-left text-xs font-semibold uppercase tracking-wide ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
                Priority
              </th>
              {/* Camera / Images column */}
              <th className={`px-3 py-3 text-center text-xs font-semibold uppercase tracking-wide ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
                Images
              </th>
            </tr>
          </thead>
          <tbody>
            {sortedRepairs.length === 0 && (
              <tr>
                <td colSpan={8} className={`text-center py-16 text-sm ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>
                  <div className="flex flex-col items-center gap-2">
                    <FileText size={32} className="opacity-30" />
                    <span>No repairs match the current filter</span>
                  </div>
                </td>
              </tr>
            )}
            {sortedRepairs.map((ticket, idx) => {
              const assetImageUrl = ticket.asset_image_url ||
                (ticket.asset_type ? getAssetImageUrl(ticket.asset_type + '.png') : null);
              const damagedItems = getDamagedItems(ticket);

              return (
                <tr
                  key={ticket.id}
                  onClick={() => onTicketClick && onTicketClick(ticket)}
                  className={`cursor-pointer transition-colors border-b ${
                    isDark
                      ? `border-slate-800 ${idx % 2 === 0 ? 'bg-slate-900/40' : 'bg-slate-800/20'} hover:bg-slate-700/50`
                      : `border-gray-100 ${idx % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'} hover:bg-blue-50/60`
                  }`}
                >
                  {/* Checkbox cell — enabled for rows the current user can act on */}
                  {(() => {
                    const isC = isCustomerForTicket(ticket);
                    const isV = isVendorForTicket(ticket);

                    const canAct = moveRepair && (
                      (isC && ticket.status === 'Needs Approval') ||
                      (isV && ticket.status === 'Needs Approval') ||
                      (isV && ticket.status === 'In Repair') ||
                      (isV && ticket.status === 'Needs Repair')
                    );
                    return (
                      <td className="px-3 py-3" onClick={e => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          checked={selectedIds.has(ticket.id)}
                          onChange={e => toggleRow(e, ticket.id)}
                          disabled={!canAct}
                          title={canAct ? 'Select for bulk action' : 'No action available for this repair'}
                          className={`rounded ${canAct ? 'cursor-pointer accent-blue-500' : 'opacity-30 cursor-not-allowed'}`}
                        />
                      </td>
                    );
                  })()}

                  {/* Asset */}
                  <td className="px-3 py-3">
                    <div className="flex items-center gap-3">
                      {assetImageUrl ? (
                        <div className="w-10 h-10 bg-slate-800 rounded-lg overflow-hidden flex-shrink-0 border border-slate-600">
                          <img
                            src={assetImageUrl}
                            alt={ticket.asset_name || 'Asset'}
                            className="w-full h-full object-contain p-1"
                            onError={e => { e.target.style.display = 'none'; }}
                          />
                        </div>
                      ) : (
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${isDark ? 'bg-slate-800 border border-slate-700' : 'bg-gray-100 border border-gray-200'}`}>
                          <Wrench size={16} className={isDark ? 'text-slate-500' : 'text-gray-400'} />
                        </div>
                      )}
                      <div className="min-w-0 flex-1">
                        <div className={`text-sm font-semibold truncate max-w-[160px] ${isDark ? 'text-slate-200' : 'text-slate-800'}`}>
                          {ticket.asset_name || ticket.asset_id}
                        </div>
                        <div className={`text-xs truncate max-w-[160px] ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>
                          ID: {ticket.asset_id}
                        </div>
                      </div>
                      {/* Inspect button for vendor Needs Repair rows */}
                      {isVendorForTicket(ticket) && ticket.status === 'Needs Repair' && onFillInspection && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            onFillInspection(ticket);
                          }}
                          title="Fill out repair inspection"
                          className={`flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-medium whitespace-nowrap transition-colors flex-shrink-0
                            ${isDark
                              ? 'bg-red-600/20 hover:bg-red-600 text-red-400 hover:text-white border border-red-500/40'
                              : 'bg-red-50 hover:bg-red-600 text-red-600 hover:text-white border border-red-300'
                            }`}
                        >
                          <Wrench size={11} />
                          Inspect
                        </button>
                      )}
                    </div>
                  </td>

                  {/* Customer */}
                  <td className="px-3 py-3">
                    <span className={`text-sm truncate max-w-[140px] block ${isDark ? 'text-slate-300' : 'text-slate-700'}`}>
                      {ticket.company_name || '—'}
                    </span>
                  </td>

                  {/* Status */}
                  <td className="px-3 py-3">
                    <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium whitespace-nowrap ${getStatusColor(ticket.status)}`}>
                      {ticket.status}
                    </span>
                  </td>

                  {/* Cost */}
                  <td className="px-3 py-3">
                    {ticket.total_repair_cost ? (
                      <span className="flex items-center gap-1 text-sm font-bold text-emerald-500 whitespace-nowrap">
                        <DollarSign size={14} />
                        {parseFloat(ticket.total_repair_cost).toFixed(2)}
                      </span>
                    ) : (
                      <span className={`text-sm ${isDark ? 'text-slate-600' : 'text-slate-400'}`}>—</span>
                    )}
                  </td>

                  {/* Description — truncated with full text on hover (H) */}
                  <td className="px-3 py-3 max-w-[200px]">
                    <span
                      className={`text-sm truncate block max-w-[200px] ${isDark ? 'text-slate-400' : 'text-slate-600'}`}
                      title={ticket.description || ticket.title || ''}
                    >
                      {ticket.description || ticket.title || '—'}
                    </span>
                  </td>

                  {/* Damaged Items pills (J) — only items flagged as Damaged in checklist */}
                  <td className="px-3 py-3">
                    {damagedItems.length > 0 ? (
                      <div className="flex flex-wrap gap-1 max-w-[200px]">
                        {damagedItems.slice(0, 3).map((item, i) => (
                          <span
                            key={item.id || i}
                            className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                              isDark
                                ? 'bg-red-500/20 text-red-300 border border-red-500/30'
                                : 'bg-red-50 text-red-700 border border-red-200'
                            }`}
                          >
                            {item.label || item.id}
                          </span>
                        ))}
                        {damagedItems.length > 3 && (
                          <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>
                            +{damagedItems.length - 3} more
                          </span>
                        )}
                      </div>
                    ) : (
                      <span className={`text-xs ${isDark ? 'text-slate-600' : 'text-slate-400'}`}>—</span>
                    )}
                  </td>

                  {/* Priority */}
                  <td className="px-3 py-3">
                    {ticket.priority ? (
                      <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                        ticket.priority === 'Critical'
                          ? isDark ? 'bg-red-500/20 text-red-300' : 'bg-red-100 text-red-700'
                          : ticket.priority === 'High'
                            ? isDark ? 'bg-orange-500/20 text-orange-300' : 'bg-orange-100 text-orange-700'
                            : ticket.priority === 'Medium'
                              ? isDark ? 'bg-yellow-500/20 text-yellow-300' : 'bg-yellow-100 text-yellow-700'
                              : isDark ? 'bg-slate-700 text-slate-400' : 'bg-gray-100 text-gray-600'
                      }`}>
                        {ticket.priority}
                      </span>
                    ) : (
                      <span className={`text-xs ${isDark ? 'text-slate-600' : 'text-slate-400'}`}>—</span>
                    )}
                  </td>

                  {/* Camera icon — opens inline image gallery (I) */}
                  <td className="px-3 py-3 text-center" onClick={e => e.stopPropagation()}>
                    <button
                      onClick={e => openGallery(e, ticket)}
                      title="View repair images"
                      className={`p-1.5 rounded-lg transition-colors ${
                        isDark
                          ? 'text-slate-400 hover:text-blue-400 hover:bg-slate-700'
                          : 'text-gray-400 hover:text-blue-600 hover:bg-blue-50'
                      }`}
                    >
                      <Camera size={16} />
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* ── Image Gallery Modal (revision I) ──────────────────────────────────── */}
      {galleryTicket && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm"
          onClick={() => { setGalleryTicket(null); setLightboxUrl(null); }}
        >
          <div
            className={`relative w-full max-w-3xl max-h-[80vh] mx-4 rounded-2xl shadow-2xl flex flex-col overflow-hidden ${isDark ? 'bg-slate-900 border border-slate-700' : 'bg-white border border-gray-200'}`}
            onClick={e => e.stopPropagation()}
          >
            {/* Header */}
            <div className={`flex items-center justify-between px-5 py-4 border-b ${isDark ? 'border-slate-700' : 'border-gray-200'}`}>
              <div>
                <h3 className={`font-semibold text-base ${isDark ? 'text-white' : 'text-slate-900'}`}>
                  Repair Images
                </h3>
                <p className={`text-xs mt-0.5 ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                  {galleryTicket.asset_name || galleryTicket.asset_id}
                </p>
              </div>
              <button
                onClick={() => { setGalleryTicket(null); setLightboxUrl(null); }}
                className={`p-2 rounded-lg transition-colors ${isDark ? 'hover:bg-slate-700 text-slate-400' : 'hover:bg-gray-100 text-gray-500'}`}
              >
                <X size={18} />
              </button>
            </div>

            {/* Body */}
            <div className="flex-1 overflow-y-auto p-5">
              {galleryLoading ? (
                <div className="flex items-center justify-center py-16">
                  <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
                </div>
              ) : (() => {
                const files = repairFiles[galleryTicket.id] || [];
                const images = files.filter(f => {
                  const url = f.viewUrl || f.view_url || f.url;
                  return url && !url.startsWith('gs://') &&
                    (f.mime_type?.startsWith('image/') || f.type?.startsWith('image/') || url);
                });

                if (images.length === 0) {
                  return (
                    <div className="flex flex-col items-center justify-center py-16 gap-3">
                      <ImageOff size={40} className={isDark ? 'text-slate-600' : 'text-gray-300'} />
                      <p className={`text-sm ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>No images attached to this repair</p>
                    </div>
                  );
                }

                return (
                  <div className="grid grid-cols-3 gap-3">
                    {images.map((file, idx) => {
                      const url = file.viewUrl || file.view_url || file.url;
                      const name = file.original_filename || file.filename || `Image ${idx + 1}`;
                      return (
                        <div
                          key={file.id || idx}
                          className={`rounded-lg overflow-hidden border cursor-pointer hover:opacity-80 transition-opacity ${isDark ? 'border-slate-700 bg-slate-800' : 'border-gray-200 bg-gray-50'}`}
                          onClick={() => setLightboxUrl({ url, name })}
                        >
                          <img
                            src={url}
                            alt={name}
                            className="w-full h-36 object-cover"
                            onError={e => { e.target.style.display = 'none'; }}
                          />
                          <p className={`px-2 py-1.5 text-xs truncate ${isDark ? 'text-slate-400' : 'text-gray-600'}`}>{name}</p>
                        </div>
                      );
                    })}
                  </div>
                );
              })()}
            </div>
          </div>
        </div>
      )}

      {/* ── Lightbox (single image full-screen) ───────────────────────────────── */}
      {lightboxUrl && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center bg-black/90"
          onClick={() => setLightboxUrl(null)}
        >
          <button
            onClick={() => setLightboxUrl(null)}
            className="absolute top-4 right-4 p-2 rounded-lg bg-white/10 hover:bg-white/20 text-white transition-colors"
          >
            <X size={20} />
          </button>
          <img
            src={lightboxUrl.url}
            alt={lightboxUrl.name}
            className="max-w-[90vw] max-h-[90vh] object-contain rounded-lg shadow-2xl"
            onClick={e => e.stopPropagation()}
          />
          <p className="absolute bottom-4 left-1/2 -translate-x-1/2 text-white/70 text-sm">{lightboxUrl.name}</p>
        </div>
      )}
    </div>
  );
};

export default RepairListView;
